
  import {computed, defineComponent, onMounted, onUnmounted, reactive, toRefs, watch, inject} from "vue";
  import {Task} from "@/model/OrderTaskImport";
  import OrderTaskService from "@/service/OrderTaskService";
  import OrderServiceV2 from "@/service/OrderServiceV2";
  import {employeeId, employeeUuid} from "@/store/StoreUtils";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import InventoriesTasksDialog from "./dialog/InventoriesTasksDialog.vue";
  import {isAuthorized, t} from "@/mixin/mixins";
  import InventoriesTasksTable from "./InventoriesTasksTable.vue";
  import SwitchView from "@/components/SwitchView.vue";
  import FabButton from "@/components/FabButton.vue";
  import InventoriesTasksList from "./InventoriesTasksList.vue";
  import StatisticService from "@/service/StatisticService";
  import { EmployeesDailyStatistic } from "@/model/EmployeesDailyStatistic";
  import moment from "moment";

  export default defineComponent({
    name: "InventoriesTasks",
    components: {
      SwitchView,
      InventoriesTasksTable,
      InventoriesTasksList,
      InventoriesTasksDialog,
      FabButton
    },
    setup() {
      const state = reactive({
        stats: null as EmployeesDailyStatistic | null,
        tasks: [] as Task[],
        // The numeric ids of the orders valid today by their uuids, needed by the daily statistics only
        orderIdsByUuid: new Map<string, number>(),
        orderId: null as string | null | undefined,
        employeeId: null as string | null | undefined,
        initialized: false as boolean
      });

      let appRoot = inject("app-root");
      // @ts-ignore
      const confirmDialog = appRoot.confirmDialogComposition as InstanceType<typeof ConfirmDialogComposition>;

      onMounted(() => {
        findOrderIds();
        findTasks();

        eventBus.$on(MessageTypes.TASKS_RELOAD, findTasks);
        eventBus.$on(MessageTypes.TASK_UPDATED, findTasks);
        eventBus.$on(MessageTypes.TASK_CREATED, findTasks);
        eventBus.$on(MessageTypes.CHECK_TASK, onCheckTask);
      });

      // Without removing the callbacks, every mount of this view adds another set of them, so each action reloads the
      // tasks (and asks for confirmation) once more per earlier mount
      onUnmounted(() => {
        eventBus.$off(MessageTypes.TASKS_RELOAD, findTasks);
        eventBus.$off(MessageTypes.TASK_UPDATED, findTasks);
        eventBus.$off(MessageTypes.TASK_CREATED, findTasks);
        eventBus.$off(MessageTypes.CHECK_TASK, onCheckTask);
      });

      const isManager = () => {
        return isAuthorized("Inventory_ViewTasksManager");
      };

      const employeeName = (task: Task) => {
        return task.employee.firstName + " " + task.employee.lastName;
      };

      const userCanGetStats = (employeeIds: number[]) => {
        if (isManager()) {
          return true;
        } else {
          if (employeeIds?.length == 1) {
            const currentUserEmployeeId = parseInt(employeeId.value);
            return employeeIds.includes(currentUserEmployeeId);
          } else {
            return false;
          }
        }
      };

      const findTasks = () => {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split("T")[0];
        OrderTaskService.findTasks(formattedDate)
          .then((response) => {
            state.tasks = response.data.tasks;
          })
          .catch((error) => {
            console.error("Error fetching tasks", error);
          });
      };

      // The tasks only name their order by uuid, but the daily statistics take numeric order ids, looked up among
      // today's orders. Only a user with the authority to list orders gets the statistics.
      const findOrderIds = () => {
        if (!isAuthorized("Order_Overview_ListOrders")) {
          return;
        }
        const today = moment().format("YYYY-MM-DD");
        OrderServiceV2.findOrdersActiveOnV3(today)
          .then(orders => {
            state.orderIdsByUuid = new Map(orders
              .filter(order => order.legacyId != null)
              .map(order => [order.id, order.legacyId as number]));
          })
          .catch((error) => {
            console.error("Error fetching orders", error);
          });
      };

      const filteredTasksOrder = computed(() => {
        let filtered = state.tasks.slice(0);
        if (state.orderId) {
          filtered = filtered.filter((task: Task) => {
            return task.order.id === state.orderId;
          });
        }
        return filtered;
      });

      const filteredTasks = computed(() => {
        let filtered = filteredTasksOrder.value.slice(0);
        if (state.employeeId) {
          filtered = filtered.filter((task: Task) => {
            return task.employee.id === state.employeeId;
          });
        }
        return filtered;
      });

      // Tasks without a priority come last
      const sortTasks = (tasks: Task[]) => {
        return tasks.sort((a, b) => {
          if (!a.priority) return 1;
          if (!b.priority) return -1;
          return a.priority - b.priority;
        });
      };

      const pendingTasks = computed(() =>
        sortTasks(filteredTasks.value.filter((task: Task) => !task.completed))
      );

      const completedTasks = computed(() =>
        sortTasks(filteredTasks.value.filter((task: Task) => task.completed))
      );

      const orderFilterOptions = computed(() => {
        const options: Array<{ label: string; orderId: string | null }> = Array.from(
          new Map(state.tasks.map((task: Task) => [task.order.id, {
            label: task.order.name,
            orderId: task.order.id
          }])).values()
        );

        options.unshift({
          label: t("inventories.tasks.filters.allOrders"),
          orderId: null
        });

        return options;
      });

      const employeeFilterOptions = computed(() => {
        const options: Array<{ label: string; employeeId: string | null }> = Array.from(
          new Map(filteredTasksOrder.value.map((task: Task) => [task.employee.id, {
            label: employeeName(task),
            employeeId: task.employee.id
          }])).values()
        );

        options.unshift({
          label: t("inventories.tasks.filters.allEmployees"),
          employeeId: null
        });
        return options;
      });

      watch(() => employeeFilterOptions, () => {
        const employeeIds = employeeFilterOptions.value.map(option => option.employeeId);
        if (state.employeeId && !employeeIds.includes(state.employeeId)) {
          state.employeeId = null;
        }

        if (!state.initialized) {
          const myId = employeeUuid.value;
          if (myId && employeeIds.includes(myId)) {
            state.employeeId = myId;
          }
          state.initialized = true;
        }

      }, { deep: true });

      const pendingPos = computed(() => {
        let pos = 0;
        pendingTasks.value.map((task: Task) => {
          pos += task.positions;
        });
        return pos;
      });

      const completedPos = computed(() => {
        let pos = 0;
        completedTasks.value.map((task: Task) => {
          pos += task.positions;
        });
        return pos;
      });

      const getDailyStatistic = () => {
        state.stats = null;

        // The daily statistics take numeric ids: the employee number, which is the employee's numeric id, and the
        // order's numeric id looked up by its uuid
        let employeeIds: number[] = [];
        filteredTasks.value.forEach((task: Task) => {
          if (task.employeeNumber && !employeeIds.includes(task.employeeNumber)) {
            employeeIds.push(task.employeeNumber);
          }
        });

        let orderIds: number[] = [];
        filteredTasks.value.forEach((task: Task) => {
          const orderId = state.orderIdsByUuid.get(task.order.id);
          if (orderId && !orderIds.includes(orderId)) {
            orderIds.push(orderId);
          }
        });

        if (orderIds.length > 0 && userCanGetStats(employeeIds)) {
          const currentDate = new Date();
          const formattedDate = currentDate.toISOString().split("T")[0];
          StatisticService.getDailyStatistic(orderIds, employeeIds, formattedDate)
            .then((response) => {
              state.stats = response.data;
              if (state.stats) state.stats.firstClockInTime = state.stats.firstClockInTime?.substring(0, 5);

              // In case we have no end time we simply use the current time as an endtime to calculate the minutes
              // from the start
              if (state.stats?.minutesToday == 0) {
                let minutes = moment().diff(moment(state.stats?.firstClockInTime, "HH:mm"), "minutes");
                state.stats.minutesToday = minutes;
              }
            });
        }
      };

      const minutesLeftPast = computed(() => {
        if (pendingPos?.value && state?.stats?.avgPosPerHourPast) {
          const pos = pendingPos.value;
          const avg = state.stats.avgPosPerHourPast;

          // Divide by the number of selected employees
          let denominator = (!!state.employeeId) ? 1 : employeeFilterOptions.value.length;
          return Math.round(((pos / avg) * 60) / denominator);
        } else {
          return null;
        }
      });

      const avgPosPerHourToday = computed(() => {
        if (completedPos?.value && state?.stats?.minutesToday) {
          return state.stats.minutesToday > 0
            ? completedPos.value / (state.stats.minutesToday / 60)
            : 0;
        } else {
          return null;
        }
      });

      const minutesLeftToday = computed(() => {
        if (pendingPos?.value && avgPosPerHourToday?.value) {
          const pos = pendingPos.value;
          const avg = avgPosPerHourToday.value;

          // Divide by the number of selected employees (subtract 1 for the "all employees" option)
          let denominator = (!!state.employeeId) ? 1 : employeeFilterOptions.value.length - 1;
          return Math.round(((pos / avg) * 60) / denominator);
        } else {
          return null;
        }
      });

      const avgPosDeviation = computed(() => {
        if (state.stats?.avgPosPerHourPast && avgPosPerHourToday?.value) {
          const deviation = ((avgPosPerHourToday.value - state.stats.avgPosPerHourPast) / state.stats.avgPosPerHourPast) * 100;
          return Math.round(deviation * 10) / 10;
        } else {
          return null;
        }
      });

      const onCheckTask = (task: Task) => {
        if (task) {
          const title = `${task.label}\n${employeeName(task)}`;
          const text = t("inventories.tasks.dialog." + (task.completed ? "uncheckTask" : "checkTask"));
          confirmDialog.open(title, text)
            .then((confirmResult: boolean) => {
              if (confirmResult) {
                toggleTask(task);
              }
            });
        }
      };

      const toggleTask = (task: Task) => {
        const request = task.completed
          ? OrderTaskService.reopenTask(task.id)
          : OrderTaskService.completeTask(task.id);

        request
          .then((response) => {
            if (response.status == 200) {
              const index = state.tasks.findIndex((t: Task) => t.id === task.id);
              if (index !== -1) {
                state.tasks.splice(index, 1, response.data);
                eventBus.$emit(MessageTypes.TASKS_RELOAD);
              }
            }
          })
          .catch((error) => {
            console.error("Error updating task:", error);
          });
      };

      watch(() => pendingTasks, () => {
        getDailyStatistic();
      }, { deep: true });

      watch(() => state.orderIdsByUuid, () => {
        getDailyStatistic();
      });

      const toTimeFormat = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const hour = h < 10 ? "0" + h : h;
        const minute = m < 10 ? "0" + m : m;
        return hour + ":" + minute;
      };

      const onOpenTaskDialog = (task: Task) => {
        const orders = orderFilterOptions.value;
        eventBus.$emit(MessageTypes.OPEN_TASK_DIALOG, task, orders.slice(1, orders.length));
      };

      return {
        ...toRefs(state),
        isManager,
        pendingTasks,
        completedTasks,
        pendingPos,
        completedPos,
        minutesLeftPast,
        minutesLeftToday,
        avgPosPerHourToday,
        avgPosDeviation,
        orderFilterOptions,
        employeeFilterOptions,
        toTimeFormat,
        onOpenTaskDialog
      };
    },
  });
